// Firebase Web SDK bootstrap for the dashboard.
//
// Only the PUBLIC web config lives here (CLAUDE.md: nothing business-critical on
// the client). The Admin service account — FIREBASE_CLIENT_EMAIL /
// FIREBASE_PRIVATE_KEY — belongs to the API and must never reach this bundle.
//
// Three rules shape this module:
//   1. Initialize once. Next's dev refresh and React Strict Mode both re-run
//      module consumers, so `getApps()` is checked before `initializeApp`.
//   2. Never touch Messaging during SSR. `firebase/messaging` reads `navigator`
//      and `window` at call time, so every accessor is browser-guarded.
//   3. Missing config must not break the dashboard. Push is an enhancement; a
//      deployment without Firebase keys still logs in, loads deals and rings
//      bells. Every accessor returns null instead of throwing.

import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  getMessaging,
  isSupported,
  type Messaging,
} from "firebase/messaging";

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

/** Reads the public web config. Returns null when any field is absent so callers
 *  can degrade instead of initializing a half-configured app. */
export function firebaseConfig(): FirebaseWebConfig | null {
  const cfg = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  };

  const missing = Object.entries(cfg).filter(([, value]) => !value || !value.trim());
  if (missing.length > 0) {
    return null;
  }
  return cfg as FirebaseWebConfig;
}

/** The public Web Push certificate key. Separate from the app config because
 *  token minting fails with a clearer error when only this one is missing. */
export function vapidKey(): string | null {
  const key = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY?.trim();
  return key ? key : null;
}

/** True when the environment carries a complete push configuration. */
export function isFirebaseConfigured(): boolean {
  return firebaseConfig() !== null && vapidKey() !== null;
}

/** Idempotent app handle. Null when unconfigured or during SSR. */
export function firebaseApp(): FirebaseApp | null {
  if (typeof window === "undefined") return null;

  const cfg = firebaseConfig();
  if (!cfg) return null;

  try {
    // getApps() is the cross-module guard: two importers still share one app.
    if (getApps().length) return getApp();
    return initializeApp(cfg);
  } catch {
    // Malformed config (bad appId shape, etc.) must not take the shell down.
    return null;
  }
}

let messagingPromise: Promise<Messaging | null> | null = null;

/** Messaging handle, or null when the browser cannot do FCM (no
 *  ServiceWorker / Push / Notification support, private mode, http origin).
 *
 *  The promise is memoized rather than the resolved value: concurrent callers —
 *  Strict Mode's double effect, most commonly — share one support check and one
 *  getMessaging() call. */
export function firebaseMessaging(): Promise<Messaging | null> {
  if (typeof window === "undefined") return Promise.resolve(null);

  if (!messagingPromise) {
    messagingPromise = (async () => {
      const app = firebaseApp();
      if (!app) {
        return null;
      }
      try {
        if (!(await isSupported())) {
          // The usual cause is an insecure origin: FCM needs https (or
          // localhost). Private browsing and older Safari also fail here.
          return null;
        }
        return getMessaging(app);
      } catch {
        return null;
      }
    })();
  }

  return messagingPromise;
}

/** Test seam — drops the memoized Messaging promise. */
export function resetFirebaseMessagingForTests(): void {
  messagingPromise = null;
}
